/**
 * Quali template preventivo sono adatti a una certa offerta.
 *
 * Un template contratto puo' valere per tutti i servizi, solo per alcuni, o per
 * tutti tranne alcuni. La regola: e' adatto se copre TUTTE le righe servizio
 * dell'offerta. Le righe prodotto non contano (in produzione sono 10 in tutto);
 * le righe fuori catalogo contano solo se la casella del template e' spenta.
 *
 * Logica pura e senza dipendenze: la usano sette punti diversi (generazione,
 * proposta, firma, allegato email, impostazioni, anteprima offerta, miniature),
 * che prima decidevano ognuno per conto proprio.
 *
 * Spec: MD/features/2026-08-12-template-preventivo-per-servizio.md
 */

export type ServiceScope = 'all' | 'only' | 'except';

export interface TemplateScope {
  id: string;
  /** Assente sui template creati prima della feature: vale 'all'. */
  service_scope?: ServiceScope | null;
  /** Assente = true. Se false, le righe fuori catalogo rendono il template non adatto. */
  applies_to_custom_lines?: boolean | null;
  /** service_id dalla tabella ponte. Il significato dipende da service_scope. */
  scopedServiceIds?: string[] | null;
}

export interface DealLines {
  /** service_id delle righe servizio a catalogo (duplicati ammessi). */
  serviceIds: string[];
  /** true se l'offerta ha almeno una riga servizio scritta a mano. */
  hasCustomLines: boolean;
}

export function isTemplateSuitable<T extends TemplateScope>(template: T, lines: DealLines): boolean {
  // La casella vale a prescindere dall'ambito: spegnerla significa "questo
  // template non e' per offerte con righe scritte a mano", punto.
  if (lines.hasCustomLines && template.applies_to_custom_lines === false) {
    return false;
  }

  const scope = template.service_scope ?? 'all';
  if (scope === 'all') return true;

  const scoped = new Set(template.scopedServiceIds ?? []);
  const used = new Set(lines.serviceIds);

  if (scope === 'only') {
    // Un'offerta senza righe servizio e' coperta da chiunque.
    for (const id of used) {
      if (!scoped.has(id)) return false;
    }
    return true;
  }

  // 'except'
  for (const id of used) {
    if (scoped.has(id)) return false;
  }
  return true;
}

export function splitBySuitability<T extends TemplateScope>(
  templates: T[],
  lines: DealLines,
): { suitable: T[]; unsuitable: T[] } {
  const suitable: T[] = [];
  const unsuitable: T[] = [];
  for (const t of templates) {
    (isTemplateSuitable(t, lines) ? suitable : unsuitable).push(t);
  }
  return { suitable, unsuitable };
}

/**
 * Il template da mostrare come anteprima di un'offerta, in ordine:
 *   1. il predefinito dell'organizzazione, se adatto;
 *   2. altrimenti il primo adatto nell'ordine ricevuto;
 *   3. altrimenti null → il chiamante mostra un segnaposto, mai un documento
 *      sbagliato.
 */
export function pickPreviewTemplate<T extends TemplateScope>(
  templates: T[],
  lines: DealLines,
  defaultTemplateId: string | null | undefined,
): T | null {
  const suitable = templates.filter((t) => isTemplateSuitable(t, lines));
  if (suitable.length === 0) return null;

  if (defaultTemplateId) {
    const preferito = suitable.find((t) => t.id === defaultTemplateId);
    if (preferito) return preferito;
  }
  return suitable[0];
}

/** Estrae `serviceIds` e `hasCustomLines` dalle righe grezze di `deal_services`. */
export function dealLinesFromRows(
  rows: Array<{ line_type?: string | null; service_id?: string | null }> | null | undefined,
): DealLines {
  const serviceIds: string[] = [];
  let hasCustomLines = false;

  for (const row of rows ?? []) {
    // `line_type` ha default 'service' nel DB, ma le righe piu' vecchie possono
    // averlo nullo: si considerano servizio.
    if ((row.line_type ?? 'service') === 'product') continue;
    if (row.service_id) serviceIds.push(row.service_id);
    else hasCustomLines = true;
  }

  return { serviceIds, hasCustomLines };
}
